import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box, Paper, Typography, Avatar, TextField, InputAdornment, Chip,
  CircularProgress, Grid, Card, CardContent, Button
} from '@mui/material';
import { Search as SearchIcon, Message, CalendarToday } from '@mui/icons-material';
import { useAuth } from '../../store/authStore';
import { getTeacherStudentsList } from '../../api/booking';
import { getOrCreateConversation } from '../../api/messages';
import { getCurrentLanguage } from '../../utils/navigation';

const TeacherStudents = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const lang = getCurrentLanguage();
  const [students, setStudents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    loadStudents();
  }, [user?.id]);

  const loadStudents = async () => {
    if (!user?.id) return;
    try {
      const data = await getTeacherStudentsList(user.id);
      setStudents(data);
    } catch (err) {
      console.error('Error loading students:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleMessage = async (student) => {
    try {
      await getOrCreateConversation(
        user.id, student.id,
        user.displayName || `${user.firstName} ${user.lastName}`,
        student.name
      );
      navigate(`/${lang}/teacher/messages`);
    } catch (err) {
      console.error('Error:', err);
    }
  };

  const filtered = students.filter(s =>
    s.name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  if (loading) {
    return <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}><CircularProgress /></Box>;
  }

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h5" fontWeight={700}>My Students</Typography>
        <Chip label={`${students.length} students`} color="primary" />
      </Box>

      <TextField
        fullWidth size="small" placeholder="Search students..."
        value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
        InputProps={{
          startAdornment: <InputAdornment position="start"><SearchIcon /></InputAdornment>
        }}
        sx={{ mb: 3 }}
      />

      {filtered.length === 0 ? (
        <Paper sx={{ p: 6, textAlign: 'center' }}>
          <Typography variant="h6" color="text.secondary">
            {students.length === 0 ? 'No students yet' : 'No matching students'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            Students will appear here after they book lessons with you
          </Typography>
        </Paper>
      ) : (
        <Grid container spacing={2}>
          {filtered.map(student => (
            <Grid item xs={12} sm={6} md={4} key={student.id}>
              <Card sx={{ '&:hover': { boxShadow: 3 }, transition: 'all 0.2s' }}>
                <CardContent>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 2 }}>
                    <Avatar sx={{ width: 56, height: 56, bgcolor: 'primary.light' }}>
                      {student.name?.charAt(0)}
                    </Avatar>
                    <Box>
                      <Typography variant="h6" fontWeight={600}>{student.name}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {student.totalLessons} lesson{student.totalLessons !== 1 ? 's' : ''}
                      </Typography>
                    </Box>
                  </Box>

                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 2 }}>
                    <CalendarToday sx={{ fontSize: 16 }} color="action" />
                    <Typography variant="body2" color="text.secondary">
                      Last lesson: {student.lastLesson || 'N/A'}
                    </Typography>
                  </Box>

                  <Button
                    variant="outlined" fullWidth startIcon={<Message />}
                    onClick={() => handleMessage(student)}
                  >
                    Send Message
                  </Button>
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}
    </Box>
  );
};

export default TeacherStudents;
